import React, { useContext, useState, useEffect, useRef } from "react";
import { SellerContext } from "./sellerContext";
import { 
    DataGridPro, 
    MuiDataGrid, 
    MuiIcons,
    apiClient, 
    setAlert, 
    AlertTypes,
    Button, 
    Tooltip, 
    IconButton, 
    Box, 
    Modal, 
    Snackbar, 
    Typography,
} from "@leap4/frontend";
import "./SellerValidation.css";
import SellerPageHeader from "./sellerPageHeader";
import { baseSellerAdsTxtCheckUrl, sellersBaseUrl } from "../../../config/generals";
import { dateToStringFormat } from "../../utils/functions";
import { ISellerJson } from "./seller.interface";
import { statusTypes } from "./enums";
import { connect, ConnectedProps } from "react-redux";

const {
    Add: AddIcon,
    Delete: DeleteIcon,
    Save: SaveIcon,
    Close: CancelIcon,
    Edit: EditIcon,
    ContentCopy: ContentCopyIcon,
    Description: DescriptionIcon,
} = MuiIcons;
// Runtime values from MuiDataGrid
const {
    GridRowModes,
    GridRowEditStopReasons,
    useGridApiContext,
    useGridApiRef,
} = MuiDataGrid;

// Types from MuiDataGrid namespace
type GridColDef = MuiDataGrid.GridColDef;
type GridRenderCellParams<V = any, R = any> = MuiDataGrid.GridRenderCellParams<V, R>;
type GridRowsProp = MuiDataGrid.GridRowsProp;
type GridRowModesModel = MuiDataGrid.GridRowModesModel;
type GridEventListener<E extends keyof MuiDataGrid.GridEventLookup> = MuiDataGrid.GridEventListener<E>;
type GridRowId = MuiDataGrid.GridRowId;
type GridRowModel = MuiDataGrid.GridRowModel;






const mapStateToProps = () => ({});

const mapActions = {
    setAlert
};

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

const SellerValidation: React.FC<PropsFromRedux> = ({ setAlert }) => {
    const { currentSeller, setCurrentSeller, site } = useContext(SellerContext);
    const apiRef = useGridApiRef();

    // State to manage rows and row modes
    const [rows, setRows] = useState<GridRowsProp>(
        currentSeller?.sellers?.map((row, index) => ({ ...row, id: index })) || []
    );

    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [editingCommentsCells, setEditingCommentsCells] = useState<Set<GridRowId>>(new Set());
    const editingCommentsCellsRef = useRef<Set<GridRowId>>(new Set());

    const [openModalId, setOpenModalId] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [sellerIdChangeModal, setSellerIdChangeModal] = useState<{
        open: boolean;
        pendingRow: GridRowModel | null;
        originalRow: GridRowModel | null;
    }>({
        open: false,
        pendingRow: null,
        originalRow: null,
    });
    
    useEffect(() => {
        const editingRowIds = Object.keys(rowModesModel).filter(
            id => rowModesModel[id]?.mode === GridRowModes.Edit
        );
        const editingRowIdsSet = new Set(editingRowIds.map(id => String(id)));
        
        setEditingCommentsCells((prev) => {
            const newSet = new Set(prev);
            let changed = false;
            prev.forEach((cellId) => {
                if (!editingRowIdsSet.has(String(cellId))) {
                    newSet.delete(cellId);
                    editingCommentsCellsRef.current.delete(cellId);
                    changed = true;
                }
            });
            return changed ? newSet : prev;
        });
        
        return undefined;
    }, [rowModesModel]);

    const CommentsCellRenderer = React.memo((params: GridRenderCellParams) => {
        const apiRef = useGridApiContext();
        const isRowInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        const isCommentsBeingEdited = editingCommentsCells.has(params.id) || editingCommentsCellsRef.current.has(params.id);
        
        if (isCommentsBeingEdited) {
            return null;
        }
        
        const hasComment = params.row.comments?.text && params.row.comments.text.trim() !== '';
        
        if (isRowInEditMode && !isCommentsBeingEdited) {
            if (!hasComment) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (editingCommentsCells.has(params.id) || editingCommentsCellsRef.current.has(params.id)) {
                                        return;
                                    }
                                    editingCommentsCellsRef.current.add(params.id);
                                    setEditingCommentsCells((prev) => new Set(prev).add(params.id));
                                    setTimeout(() => {
                                        try {
                                            apiRef.current.startCellEditMode({ id: params.id, field: params.field });
                                        } catch (error) {
                                            console.error('Error starting cell edit mode:', error);
                                            editingCommentsCellsRef.current.delete(params.id);
                                            setEditingCommentsCells((prev) => {
                                                const newSet = new Set(prev);
                                                newSet.delete(params.id);
                                                return newSet;
                                            });
                                        }
                                    }, 0);
                                }}
                                sx={{ margin: 0 }}
                            >
                                <EditIcon />
                            </IconButton>
                    </Box>
                );
            }
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                    <Tooltip title={`${dateToStringFormat(params.row.comments.date)}`}>
                        <span>{params.row.comments.text}</span>
                    </Tooltip>
                </Box>
            );
        }
        
        if (!hasComment) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => {
                            if (editingCommentsCells.has(params.id) || editingCommentsCellsRef.current.has(params.id)) {
                                return;
                            }
                            editingCommentsCellsRef.current.add(params.id);
                            setEditingCommentsCells((prev) => new Set(prev).add(params.id));
                            
                            const isRowInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
                            
                            if (isRowInEditMode) {
                                setTimeout(() => {
                                    try {
                                        apiRef.current.startCellEditMode({ id: params.id, field: params.field });
                                    } catch (error) {
                                        console.error('Error starting cell edit mode:', error);
                                        editingCommentsCellsRef.current.delete(params.id);
                                        setEditingCommentsCells((prev) => {
                                            const newSet = new Set(prev);
                                            newSet.delete(params.id);
                                            return newSet;
                                        });
                                    }
                                }, 0);
                            } else {
                                setRowModesModel((prevModel) => ({
                                    ...prevModel,
                                    [params.id]: { mode: GridRowModes.Edit },
                                }));
                                setTimeout(() => {
                                    try {
                                        if (apiRef.current) {
                                            apiRef.current.startCellEditMode({ id: params.id, field: params.field });
                                        }
                                    } catch (error) {
                                        console.error('Error starting cell edit mode:', error);
                                        editingCommentsCellsRef.current.delete(params.id);
                                        setEditingCommentsCells((prev) => {
                                            const newSet = new Set(prev);
                                            newSet.delete(params.id);
                                            return newSet;
                                        });
                                    }
                                }, 50);
                            }
                        }}
                        sx={{ margin: 0 }}
                    >
                        <EditIcon />
                    </IconButton>
                </Box>
            );
        }
        
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%' }}>
                <Tooltip title={`${dateToStringFormat(params.row.comments.date)}`}>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.row.comments.text}</span>
                </Tooltip>
            </Box>
        );
    });

    const CommentsEditCellRenderer = React.memo((params: GridRenderCellParams) => {
        const apiRef = useGridApiContext();
        const inputRef = React.useRef<HTMLInputElement>(null);
        const hasSetEditingState = React.useRef(false);
        const hasFocusedRef = React.useRef(false);
        const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
        
        React.useEffect(() => {
            if (!hasSetEditingState.current && !editingCommentsCellsRef.current.has(params.id)) {
                editingCommentsCellsRef.current.add(params.id);
                setEditingCommentsCells((prev) => new Set(prev).add(params.id));
                hasSetEditingState.current = true;
                hasFocusedRef.current = false;
            }
            if (inputRef.current && !hasFocusedRef.current) {
                const timeoutId = setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                        hasFocusedRef.current = true;
                    }
                }, 0);
                return () => clearTimeout(timeoutId);
            }
            
            return () => {
                if (!editingCommentsCellsRef.current.has(params.id)) {
                    hasSetEditingState.current = false;
                    hasFocusedRef.current = false;
                }
                if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                }
            };
        }, [params.id]);
        
        return (
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%', 
                height: '100%',
                padding: '0 4px',
                boxSizing: 'border-box'
            }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={params.value || ''}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        apiRef.current.setEditCellValue({ 
                            id: params.id, 
                            field: params.field, 
                            value: newValue 
                        });
                    }}
                    onBlur={(e) => {
                        if (blurTimeoutRef.current) {
                            clearTimeout(blurTimeoutRef.current);
                        }
                        
                        const relatedTarget = e.relatedTarget as HTMLElement;
                        const isClickingOnGrid = relatedTarget && (
                            relatedTarget.closest('.MuiDataGrid-root') !== null ||
                            relatedTarget.closest('[role="gridcell"]') !== null ||
                            relatedTarget.closest('[role="row"]') !== null ||
                            relatedTarget.closest('input') !== null ||
                            relatedTarget.closest('button') !== null ||
                            relatedTarget.closest('textarea') !== null
                        );
                        
                        if (!isClickingOnGrid) {
                            blurTimeoutRef.current = setTimeout(() => {
                                if (editingCommentsCellsRef.current.has(params.id) && 
                                    document.activeElement !== inputRef.current &&
                                    !inputRef.current?.contains(document.activeElement)) {
                                    editingCommentsCellsRef.current.delete(params.id);
                                    setEditingCommentsCells((prev) => {
                                        const newSet = new Set(prev);
                                        newSet.delete(params.id);
                                        return newSet;
                                    });
                                    try {
                                        if (apiRef.current) {
                                            apiRef.current.stopCellEditMode({ 
                                                id: params.id, 
                                                field: params.field, 
                                                ignoreModifications: true 
                                            });
                                        }
                                    } catch (error) {
                                        // Ignore errors
                                    }
                                }
                            }, 100);
                        }
                    }}
                    onFocus={(e) => {
                        if (blurTimeoutRef.current) {
                            clearTimeout(blurTimeoutRef.current);
                            blurTimeoutRef.current = null;
                        }
                    }}
                    style={{ 
                        width: '100%',
                        border: 'none', 
                        outline: 'none', 
                        background: 'transparent',
                        fontSize: 'inherit',
                        fontFamily: 'inherit',
                        padding: 0,
                        margin: 0
                    }}
                />
            </Box>
        );
    });

    const handleOpenModal = (id) => {
        setOpenModalId(id);
    };

    const handleCloseModal = () => {
        setOpenModalId(null);
    };

    const handleCopy = (adsTxtLine) => {
        navigator.clipboard.writeText(adsTxtLine);
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    /**
     * The line to hand this seller for their app-ads.txt.
     *
     * Always DIRECT. This used to branch on seller_type and emit RESELLER for an intermediary,
     * which meant this button and the Lines Generator produced different lines for the same
     * seller. Standardised on DIRECT (Nadav, Sep 2026) because that's what's already in
     * publishers' live files; see relationshipForOwnLine in the backend's appAdsLines/lib for
     * the full reasoning and the one place to change it.
     *
     * It also used to leave the relationship empty for an unrecognised seller_type, emitting a
     * malformed "domain, id, " with a trailing comma.
     */
    const getAdsTxtLine = (row) => {
        const domainsite = site.domain || '';
        const sellerId = row.seller_id || '';
        return `${domainsite}, ${sellerId}, DIRECT`;
    };




    // Update rows when currentSeller.sellers changes
    useEffect(() => {
        if (currentSeller?.sellers) {
            const sortedRows = currentSeller.sellers
                .map((row, index) => ({ ...row, id: index }))
                .sort((a, b) => {
                    // Prioritize rows with isNew: true at the top
                    const isNewA = (a as any).isNew ? -20 : 0;
                    const isNewB = (b as any).isNew ? -20 : 0;

                    const isCreatedA = a.validation?.status?.type === statusTypes.CREATED ? 10 : -10;
                    const isCreatedB = b.validation?.status?.type === statusTypes.CREATED ? 10 : -10;

                    const isInAppAdsTxtA = a.validation?.isInAppAdsTxt  ? 1 : 0;
                    const isInAppAdsTxtB = b.validation?.isInAppAdsTxt  ? 1 : 0;

                    // Sort rows with isNew: true first, then all rows with the "CREATED" status last and all rows with "isInAppAdsTxt" set to false first
                    return isNewA - isNewB + isCreatedA - isCreatedB + isInAppAdsTxtA - isInAppAdsTxtB ;
                });
            setRows(sortedRows);
        }
    }, [currentSeller?.sellers]);

    if (!currentSeller || !currentSeller.sellers) return null;

    const resolveIssues = async (sellerId) => {
        // Make an API call to update the validation status to "Pending"
        const seller = (
            await apiClient.patch(`${sellersBaseUrl}/resolveSellerIssues/${sellerId}`)
        ).data as ISellerJson;
        setCurrentSeller(seller);
    };

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const isCellEditable = (params) => {
        if (params.field === "comments") {
            return editingCommentsCells.has(params.id) || editingCommentsCellsRef.current.has(params.id);
        }
        if (params.field === "seller_id") {
            return true;
        }
        return params.row.isNew;
    };

    const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleSaveClick = (id: GridRowId) => async () => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: {mode: GridRowModes.View},
        }));
    };

    const handleDeleteClick = (id: GridRowId) => async () => {
        const rowToDelete = rows.find((row) => row.id === id);
            try {
                const newSeller=await apiClient.delete(
                    `${sellersBaseUrl}/deleteSellerItem/${rowToDelete.seller_id}`
                );
                console.log("newSeller delete", newSeller);
                setRows(rows.filter((row) => row.id !== id));
                setCurrentSeller(newSeller.data);
            } catch (error) {
                console.error(error);
                //Handle error
                setAlert({ msg: "Error deleting seller", type: AlertTypes.FAILURE });
            }
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel((prevModel) => ({
            ...prevModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow && editedRow.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const handleSellerIdChangeDiscard = () => {
        if (sellerIdChangeModal.originalRow) {
            const originalRow = sellerIdChangeModal.originalRow;
            setRows(rows.map((row) => (row.id === originalRow.id ? originalRow : row)));
            setRowModesModel((prevModel) => ({
                ...prevModel,
                [originalRow.id]: { mode: GridRowModes.View, ignoreModifications: true },
            }));
        }
        setSellerIdChangeModal({ open: false, pendingRow: null, originalRow: null });
    };

    const handleSellerIdChangeSave = async () => {
        const { pendingRow, originalRow } = sellerIdChangeModal;
        if (!pendingRow) return;

        try {
            const originalSellerId = originalRow?.seller_id;
            const response = await apiClient.patch(`${sellersBaseUrl}/updateSellerItem`, {
                sellerJsonId: currentSeller._id,
                sellerItem: pendingRow,
                originalSellerId: originalSellerId,
            });
            
            const updatedRow = { ...pendingRow, isNew: false };
            setRows(rows.map((row) => (row.id === pendingRow.id ? updatedRow : row)));
            setCurrentSeller(response.data);
            setAlert({ msg: "Seller updated successfully", type: AlertTypes.SUCCESS });
            setRowModesModel((prevModel) => ({
                ...prevModel,
                [pendingRow.id]: { mode: GridRowModes.View },
            }));
        } catch (error: any) {
            console.error("Error processing row update:", error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Error processing row update";
            setAlert({ msg: errorMessage, type: AlertTypes.FAILURE });
            if (originalRow) {
                setRows(rows.map((row) => (row.id === originalRow.id ? originalRow : row)));
            }
        } finally {
            setSellerIdChangeModal({ open: false, pendingRow: null, originalRow: null });
        }
    };




    const processRowUpdate = async (newRow: GridRowModel) => {
        console.log("Processing row update:", newRow);
        let updatedRow;
        let response;

        if(!newRow.name || !newRow.domain) {
            setAlert({ msg: "Name and domain are required fields.", type: AlertTypes.FAILURE });
            return newRow;
        }

        if (!newRow.seller_id || newRow.seller_id.toString().trim() === '') {
            setAlert({ msg: "Seller ID is required.", type: AlertTypes.FAILURE });
            return newRow;
        }

        const parsedSellerId = parseInt(newRow.seller_id.toString().trim(), 10);
        if (isNaN(parsedSellerId)) {
            setAlert({ msg: "Seller ID must be a valid number", type: AlertTypes.FAILURE });
            return newRow;
        }

        const originalRow = rows.find((row) => row.id === newRow.id);
        const originalSellerId = originalRow?.seller_id;

        if (newRow.isNew) {
            const duplicateSeller = currentSeller.sellers.find(
                (seller) => seller.seller_id === newRow.seller_id.toString(),
            );
            if (duplicateSeller) {
                setAlert({ msg: "Seller ID already exists", type: AlertTypes.FAILURE });
                if (originalRow) {
                    return { ...originalRow, seller_id: originalRow.id || originalRow.seller_id };
                }
                return { ...newRow, seller_id: newRow.id };
            }
        } else {
            const duplicateSeller = currentSeller.sellers.find(
                (seller) =>
                    seller.seller_id === newRow.seller_id.toString() &&
                    seller.seller_id !== originalSellerId,
            );
            if (duplicateSeller) {
                setAlert({ msg: "Seller ID already exists", type: AlertTypes.FAILURE });
                if (originalRow) {
                    return originalRow;
                }
                return { ...newRow, seller_id: originalSellerId };
            }
        }

        try {
            if (newRow.isNew) {
                delete newRow.isNew;

                 response = await apiClient.post(`${sellersBaseUrl}/addSeller`, {
                    sellerJsonId: currentSeller._id,
                    sellerItem: newRow,
                });
                updatedRow = { ...newRow, isNew: false };
            } else {
                const sellerIdChanged = newRow.seller_id.toString() !== originalSellerId?.toString();
                
                if (sellerIdChanged) {
                    setSellerIdChangeModal({
                        open: true,
                        pendingRow: newRow,
                        originalRow: originalRow || null,
                    });
                    return originalRow || newRow;
                }
                
                 response = await apiClient.patch(`${sellersBaseUrl}/updateSellerItem`, {
                    sellerJsonId: currentSeller._id,
                    sellerItem: newRow,
                    originalSellerId: undefined,
                });
                updatedRow = { ...newRow, isNew: false };

            }
            setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
            setCurrentSeller(response.data);
            setAlert({ msg: "Seller updated successfully", type: AlertTypes.SUCCESS });
        } catch (error: any) {
            console.error("Error processing row update:", error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Error processing row update";
            setAlert({ msg: errorMessage, type: AlertTypes.FAILURE });
            
            const originalRow = rows.find((row) => row.id === newRow.id);
            if (originalRow) {
                return originalRow;
            }
            
            // Fallback: return the newRow but this shouldn't happen
            return newRow;
        }

        return updatedRow;
    };

    const getRowClassName = (params: any) => {
        const isInAppAdsTxt = params.row.validation?.isInAppAdsTxt;

        // Apply 'row-error' class if either is "No"
        if (!isInAppAdsTxt) {
            return "row-warning";
        }
        return "";
    };

    const handleCellDoubleClick = (params, event) => {
        if (params.field === "comments") {
            // Don't do anything if already editing this cell
            if (editingCommentsCells.has(params.id) || editingCommentsCellsRef.current.has(params.id)) {
                return;
            }
            // Add this cell to editing set (allow multiple simultaneous edits)
            editingCommentsCellsRef.current.add(params.id);
            setEditingCommentsCells((prev) => new Set(prev).add(params.id));
            setRowModesModel((prevModel) => ({
                ...prevModel,
                [params.id]: { mode: GridRowModes.Edit },
            }));
        } else if (params.field === "seller_id") {
            // Don't clear comments editing state - allow multiple simultaneous edits
            setRowModesModel((prevModel) => ({
                ...prevModel,
                [params.id]: { mode: GridRowModes.Edit, fieldToFocus: "seller_id" },
            }));
        } else if (!params.row.isNew) {
            event.defaultMuiPrevented = true;
        }
    };



    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            width: 200,
            editable: true, // Only new rows will be editable due to event handlers
        },
        {
            field: "domain",
            headerName: "Domain",
            width: 200,
            editable: true,
        },
        {
            field: "seller_id",
            headerName: "Seller ID",
            width: 100,
            editable: true,
            align: "left",
            headerAlign: "left",
            valueParser: (value) => {
                // Ensure value is a string and validate it's numeric
                const stringValue = String(value || '').trim();
                return stringValue;
            },
        },
        {
            field: "seller_type",
            headerName: "Seller Type",
            width: 100,
            type: "singleSelect",
            valueOptions: ["PUBLISHER", "INTERMEDIARY", "BOTH"],
            editable: true,
        },
        {
            field: "validation.isInAdsTxt",
            headerName: "Is In Ads.txt",
            width: 150,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.validation?.isInAdsTxt ? "Yes" : "No";
            },
        },
        {
            field: "validation.isInAppAdsTxt",
            headerName: "Is In App-Ads.txt",
            width: 150,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.validation?.isInAppAdsTxt ? "Yes" : "No";
            },
        },
        {
            field: "validation.type",
            headerName: "Relationship",
            width: 150,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.validation?.type;
            },
        },
        {
            field: "validation.error",
            headerName: "Error",
            width: 200,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.validation?.error;
            },
            renderCell: (params: GridRenderCellParams) => {
                const errorText = params.row.validation?.error;
                if (!errorText) return '';
                return (
                    <Tooltip title={errorText}  placement="top-start" >
                        <span>{errorText}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: "validation.status",
            headerName: "Status",
            width: 200,
            renderCell: (params: GridRenderCellParams) => {
                const status = params.row.validation?.status;
                if (status?.type === statusTypes.NOT_VALIDATED) {
                    return (
                            <Tooltip title={`Not validated since ${dateToStringFormat(status.date)}${params.row.validation.lastValidationStatus ? 
                            `, last changed from ${params.row.validation.lastValidationStatus.type}
                             on ${dateToStringFormat(params.row.validation.lastValidationStatus.date)}` : ''}`}>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={() => resolveIssues(params.row.seller_id)}
                            >
                                Resolve Issues
                            </Button>
                        </Tooltip>
                    );
                } else if (status?.type === statusTypes.VALIDATED) {

                   return <Tooltip
                        title={`updated from ${dateToStringFormat(
                            status?.date
                        )}${params.row.validation.lastValidationStatus ?
                            `, last changed from ${params.row.validation.lastValidationStatus.type}
                             on ${dateToStringFormat(params.row.validation.lastValidationStatus.date)}` : ''}`}
                    >
                        <span>Valid</span>
                    </Tooltip>
                } else if (status?.type === statusTypes.CREATED) {
                    return (
                        <Tooltip
                            title={`Created on ${dateToStringFormat(
                                status?.date
                            )} and not added to the seller file`}
                        >
                            <span>{statusTypes.CREATED}</span>
                        </Tooltip>
                    );
                } else {
                    // PENDING
                    return (
                        <Tooltip title={`Pending since ${dateToStringFormat(status?.date)}`}>
                            <span>{statusTypes.PENDING}</span>
                        </Tooltip>
                    );
                }
            },
        },
        {
            field: "comments",
            headerName: "Comments",
            width: 200,
            editable: true,
            valueGetter: (value, row) => row.comments?.text || '',
            valueSetter: (value, row) => {
                const newComments = { text: value, date: new Date() };
                return { ...row, comments: newComments };
            },
            renderCell: (params) => <CommentsCellRenderer {...params} />,
            renderEditCell: (params) => <CommentsEditCellRenderer {...params} />,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            width: 100,
            cellClassName: "actions",
            renderCell: (params) => {
                const { id } = params;
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return (
                        <>
                            <IconButton color="primary" aria-label="save" onClick={handleSaveClick(id)}>
                                <SaveIcon />
                            </IconButton>
                            <IconButton color="inherit" aria-label="cancel" onClick={handleCancelClick(id)}>
                                <CancelIcon />
                            </IconButton>
                        </>
                    );
                }
                const status = params.row.validation?.status;

                if (status?.type === statusTypes.CREATED || params.row.isNew) {
                    // Hide edit action for existing rows
                    return (
                        <>
                            <IconButton
                                color="inherit"
                                aria-label="delete"
                                onClick={handleDeleteClick(id)}
                            >
                                <DeleteIcon />
                            </IconButton>
                            <IconButton
                                color="inherit"
                                aria-label="View Ads.txt"
                                onClick={()=>handleOpenModal(id)}
                            >
                                <DescriptionIcon />
                            </IconButton>
                        </>
                    );
                }
                return (
                            <IconButton
                                color="inherit"
                                aria-label="View Ads.txt"
                                onClick={()=>handleOpenModal(id)}
                            >
                                <DescriptionIcon />
                            </IconButton>
                );
            },
        },
    ];

    return (
        <>
            <SellerPageHeader
                currentSeller={currentSeller}
                siteDomainName={site.domain}
                title={"Sellers.json Validation"}
                action={
                    <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={async () => {
                        const sellerId = (await apiClient.get(`${baseSellerAdsTxtCheckUrl}/generateSellerId`)).data.toString();
                        const id = sellerId;
                        setRows((oldRows) => [
                            {
                                id,
                                name: "",
                                seller_id: sellerId,
                                isNew: true,
                                validation: {
                                    isInAdsTxt: false,
                                    isInAppAdsTxt: false,
                                    type: "Invalid",
                                    status: { type: "CREATED", date: new Date() },
                                },
                            },
                            ...oldRows,
                        ]);
                        setRowModesModel((oldModel) => ({
                            ...oldModel,
                            [id]: { mode: GridRowModes.Edit, fieldToFocus: "name" },
                        }));
                    }}>
                        New Publisher
                    </Button>
                }
            />
            <Box
                sx={{
                    height: 500,
                    width: "100%",
                    "& .actions": {
                        color: "text.secondary",
                    },
                    "& .textPrimary": {
                        color: "text.primary",
                    },
                }}
            >
                {/* TODO: FIX FOR VERSION 19.2.0 OF FRONTEND LIB - Remove disableColumnPinning once lib is updated */}
                <DataGridPro
                    disableColumnPinning={true}
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowModesModelChange={handleRowModesModelChange}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    isCellEditable={isCellEditable}
                    getRowClassName={getRowClassName}
                    getRowId={(row) => row.id}
                    sx={{
                        '& .MuiDataGrid-row.row-warning': {
                            backgroundColor: 'rgba(255, 255, 0, 0.2) !important',
                        },
                        '& .MuiDataGrid-row.row-warning.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 0, 0.3) !important',
                        },
                        '& .MuiDataGrid-row.row-warning:hover': {
                            backgroundColor: 'rgba(255, 255, 0, 0.25) !important',
                        },
                    }}


                    onCellDoubleClick={handleCellDoubleClick}
                    onCellEditStop={(params, event) => {
                        // When a comment cell exits edit mode, remove it from the editing set
                        if (params.field === 'comments') {
                            editingCommentsCellsRef.current.delete(params.id);
                            setEditingCommentsCells((prev) => {
                                const newSet = new Set(prev);
                                newSet.delete(params.id);
                                return newSet;
                            });
                        }
                    }}

                />
                {openModalId !== null && (() => {
                    const selectedRow = rows.find(row => row.id === openModalId);
                    const adsTxtLine = getAdsTxtLine(selectedRow);
                    return (
                        <Modal
                            open={true}
                            onClose={handleCloseModal}
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                boxShadow: 24,
                                p: 4,
                            }}>
                                <Typography variant="h6" component="h2" gutterBottom>
                                    Ads.txt Entry
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
                                    {adsTxtLine}
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopy(adsTxtLine)}
                                >
                                    Copy
                                </Button>
                            </Box>
                        </Modal>
                    );
                })()}
                <Snackbar
                    open={snackbarOpen}
                    onClose={handleSnackbarClose}
                    message="Copied to clipboard"
                    autoHideDuration={6000}
                />
                <Modal
                    open={sellerIdChangeModal.open}
                    onClose={handleSellerIdChangeDiscard}
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 500,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 24,
                        p: 4,
                    }}>
                        <Typography variant="h6" component="h2" gutterBottom>
                            You have edited a seller ID!
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2, mb: 4 }}>
                            Please note that both ads.txt and sellers.json must be updated separately in other entities.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleSellerIdChangeDiscard}
                                sx={{ color: 'text.secondary', borderColor: 'text.secondary' }}
                            >
                                Discard
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSellerIdChangeSave}
                                sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Modal>
            </Box>
        </>
    );
};

export default connector(SellerValidation);
